import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { deleteCalendarEventFull } from "@/lib/calendarApi";
import { useAuth } from "@/lib/authContext";
import { useConfirm } from "@/lib/confirmContext";
import { useToast } from "@/hooks/use-toast";
import { ScheduleMeetingModal } from "@/components/custom/ScheduleMeetingModal";
import { EventCard } from "@/components/custom/calendar/EventCard";
import { EditEventModal } from "@/components/custom/calendar/EditEventModal";
import { BlockTimeModal } from "@/components/custom/calendar/BlockTimeModal";
import { useCalendarEvents } from "@/components/custom/calendar/useCalendarEvents";
import type { CalEvent } from "@/components/custom/calendar/types";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Plus,
  Users,
  Clock,
  Search,
} from "lucide-react";

// ── Date helpers (no external libs) ──────────────────────────────────────────

/** Returns Monday of the ISO week containing `d` (UTC-safe). */
function getWeekStart(d: Date): Date {
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day;
  const mon = new Date(d);
  mon.setDate(d.getDate() + diff);
  mon.setHours(0, 0, 0, 0);
  return mon;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function toISOLocal(d: Date): string {
  // Returns YYYY-MM-DDTHH:mm:ss.000Z using local midnight → just use toISOString
  return d.toISOString();
}

function formatWeekRange(mon: Date): string {
  const fri = addDays(mon, 4);
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  if (mon.getFullYear() === fri.getFullYear()) {
    return `${fmt(mon)} – ${fmt(fri)}, ${mon.getFullYear()}`;
  }
  return `${fmt(mon)}, ${mon.getFullYear()} – ${fmt(fri)}, ${fri.getFullYear()}`;
}

function formatDayHeader(d: Date): { weekday: string; date: string; isToday: boolean } {
  const today = new Date();
  return {
    weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
    date: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    isToday: isSameDay(d, today),
  };
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function EventSkeleton() {
  return (
    <div className="space-y-2">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="animate-pulse rounded-lg bg-muted h-14" />
      ))}
    </div>
  );
}

// ── Schedule-meeting launcher (picks client first) ────────────────────────────

function SchedulingFromCalendar({ onClose }: { onClose: () => void }) {
  const [accountId, setAccountId] = useState<string>("");
  const [search, setSearch] = useState("");
  const { data: accounts = [] } = useQuery({
    queryKey: ["accounts"],
    queryFn: () => api.getAccounts(),
    staleTime: 5 * 60 * 1000,
  });
  const chosen = (accounts as any[]).find((a) => a.id === accountId);
  const { data: chosenContacts = [] } = useQuery({
    queryKey: ["contacts", accountId],
    queryFn: () => api.getContacts({ accountId }),
    enabled: !!accountId,
    staleTime: 60 * 1000,
  });

  if (accountId && chosen) {
    return (
      <ScheduleMeetingModal
        accountId={accountId}
        accountName={chosen.account_name}
        contacts={chosenContacts}
        onClose={onClose}
      />
    );
  }

  const filtered = (accounts as any[])
    .filter((a) => a.status !== "inactive")
    .filter((a) => !search.trim() || a.account_name.toLowerCase().includes(search.toLowerCase()))
    .sort((a: any, b: any) => a.account_name.localeCompare(b.account_name));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-card border rounded-xl shadow-xl p-6 w-full max-w-sm space-y-4">
        <h2 className="text-base font-semibold">Schedule a Meeting</h2>
        <p className="text-sm text-muted-foreground">Select the client this meeting is for:</p>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
          <Input
            autoFocus
            placeholder="Search clients…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground px-3 py-3">No clients found.</p>
          ) : filtered.map((a: any) => (
            <button
              key={a.id}
              className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors"
              onClick={() => setAccountId(a.id)}
            >
              {a.account_name}
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Calendar() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [weekStart, setWeekStart] = useState<Date>(() => getWeekStart(new Date()));
  const [schedulingOpen, setSchedulingOpen] = useState(false);
  const [blockTimeOpen, setBlockTimeOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<string>("self");
  const [editingEvent, setEditingEvent] = useState<CalEvent | null>(null);
  const { toast } = useToast();
  const askConfirm = useConfirm();

  const invalidateEvents = () => queryClient.invalidateQueries({ queryKey: ["calendar-events"] });

  const deleteMutation = useMutation({
    mutationFn: (eventId: string) => deleteCalendarEventFull(eventId),
    onSuccess: () => {
      toast({ title: "Event deleted" });
      invalidateEvents();
    },
    onError: (err: Error) => toast({ title: "Failed to delete event", description: err.message, variant: "destructive" }),
  });

  async function handleDelete(event: CalEvent) {
    const title = event.type === "occurrence" || event.type === "exception"
      ? "Delete this occurrence?"
      : event.type === "seriesMaster"
        ? "Delete the entire recurring series?"
        : "Delete this event?";
    const ok = await askConfirm({
      title,
      name: event.subject ?? undefined,
      subject: "event",
      body: "will be deleted.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (ok) deleteMutation.mutate(event.id);
  }

  async function handleRemoveFromMine(event: CalEvent) {
    const ok = await askConfirm({
      title: "Remove this event from your calendar?",
      name: event.subject ?? undefined,
      subject: "event",
      body: "will be removed from your calendar.",
      lines: ["This declines the invite for you only."],
      confirmLabel: "Remove",
      destructive: true,
    });
    if (ok) deleteMutation.mutate(event.id);
  }

  const weekEnd = useMemo(() => {
    const end = addDays(weekStart, 6);
    end.setHours(23, 59, 59, 999);
    return end;
  }, [weekStart]);

  const weekDays = useMemo(
    () => Array.from({ length: 5 }, (_, i) => addDays(weekStart, i)), // Mon–Fri
    [weekStart]
  );

  // All active staff for the calendar switcher
  const { data: allUsers = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.getUsers(),
    staleTime: 10 * 60 * 1000,
  });
  const staffUsers = allUsers.filter((u: any) => u.is_active !== false && u.email);

  const targetEmail = selectedEmail === "self" ? undefined : selectedEmail;

  const { events, configured, isLoading, isError } = useCalendarEvents(
    toISOLocal(weekStart),
    toISOLocal(weekEnd),
    targetEmail
  );

  // Group events by calendar day
  const eventsByDay = useMemo(() => {
    const map = new Map<number, { allDay: CalEvent[]; timed: CalEvent[] }>();
    for (let i = 0; i < 5; i++) {
      map.set(i, { allDay: [], timed: [] });
    }
    for (const ev of events) {
      const evDate = new Date(ev.start.dateTime);
      for (let i = 0; i < 5; i++) {
        if (isSameDay(evDate, weekDays[i])) {
          if (ev.isAllDay) {
            map.get(i)!.allDay.push(ev);
          } else {
            map.get(i)!.timed.push(ev);
          }
          break;
        }
      }
    }
    return map;
  }, [events, weekDays]);

  const goBack = () => setWeekStart(prev => addDays(prev, -7));
  const goForward = () => setWeekStart(prev => addDays(prev, 7));
  const goToToday = () => setWeekStart(getWeekStart(new Date()));

  const isCurrentWeek = isSameDay(weekStart, getWeekStart(new Date()));

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
              <CalendarDays className="w-7 h-7 text-primary" />
              {selectedEmail === "self"
                ? "My Calendar"
                : (staffUsers.find((u: any) => u.email === selectedEmail)?.name ?? "Calendar")}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">{formatWeekRange(weekStart)}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Staff calendar picker — managers and admins only */}
            {(user?.role === "manager" || user?.role === "admin") && (
              <Select value={selectedEmail} onValueChange={setSelectedEmail}>
                <SelectTrigger className="h-8 w-44 text-sm gap-1">
                  <Users className="w-3.5 h-3.5 text-muted-foreground" />
                  <SelectValue placeholder="My calendar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">My calendar</SelectItem>
                  {staffUsers.map((u: any) => (
                    <SelectItem key={u.id} value={u.email}>
                      {u.name || u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Week navigation */}
            <Button variant="outline" size="sm" onClick={goBack}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant={isCurrentWeek ? "default" : "outline"}
              size="sm"
              onClick={goToToday}
            >
              This Week
            </Button>
            <Button variant="outline" size="sm" onClick={goForward}>
              <ChevronRight className="w-4 h-4" />
            </Button>

            {/* Block Time */}
            <Button size="sm" variant="outline" onClick={() => setBlockTimeOpen(true)}>
              <Clock className="w-4 h-4 mr-1.5" />
              Block Time
            </Button>

            {/* Schedule Meeting */}
            <Button size="sm" onClick={() => setSchedulingOpen(true)}>
              <Plus className="w-4 h-4 mr-1.5" />
              Schedule Meeting
            </Button>
          </div>
        </div>

        {/* Block time modal */}
        {blockTimeOpen && (
          <BlockTimeModal
            onClose={() => setBlockTimeOpen(false)}
            onSaved={() => queryClient.invalidateQueries({ queryKey: ["calendar-events"] })}
          />
        )}

        {/* Schedule meeting modal — requires selecting a client first */}
        {schedulingOpen && (
          <SchedulingFromCalendar onClose={() => setSchedulingOpen(false)} />
        )}

        {/* Edit event modal — own-calendar, isOrganizer-only events */}
        {editingEvent && (
          <EditEventModal
            event={editingEvent}
            onClose={() => setEditingEvent(null)}
            onSaved={invalidateEvents}
          />
        )}

        {/* Not configured message */}
        {!isLoading && !configured && (
          <div className="rounded-lg border border-dashed border-border bg-muted/30 p-8 text-center space-y-2">
            <CalendarDays className="w-10 h-10 text-muted-foreground mx-auto" />
            <p className="font-medium text-foreground">Outlook calendar not connected</p>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              To enable calendar sync, an admin needs to add{" "}
              <code className="bg-muted px-1 rounded text-xs">Calendars.Read</code> and{" "}
              <code className="bg-muted px-1 rounded text-xs">Calendars.ReadWrite</code> application
              permissions in the Azure portal for the Microsoft 365 integration, then grant admin
              consent. (<code className="bg-muted px-1 rounded text-xs">Calendars.ReadWrite</code>{" "}
              is required for the Block Time feature.)
            </p>
            <p className="text-xs text-muted-foreground">
              Settings → Admin → Email Configuration to verify M365 credentials are configured.
            </p>
          </div>
        )}

        {/* Error state */}
        {isError && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center">
            <p className="text-sm text-destructive">Failed to load calendar events. Please try again.</p>
          </div>
        )}

        {/* Calendar grid: Mon–Fri columns */}
        {(configured || isLoading) && !isError && (
          <div className="grid grid-cols-5 gap-3 min-w-0">
            {weekDays.map((day, i) => {
              const { weekday, date, isToday } = formatDayHeader(day);
              const dayEvents = eventsByDay.get(i) ?? { allDay: [], timed: [] };
              const totalEvents = dayEvents.allDay.length + dayEvents.timed.length;

              return (
                <div key={i} className="min-w-0 space-y-2">
                  {/* Day header */}
                  <div
                    className={cn(
                      "rounded-lg px-3 py-2 text-center border",
                      isToday
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-muted/40 border-border text-muted-foreground"
                    )}
                  >
                    <p className="text-xs font-semibold uppercase tracking-wide">{weekday}</p>
                    <p className={cn("text-sm font-bold", isToday ? "" : "text-foreground")}>{date}</p>
                    {!isLoading && totalEvents > 0 && (
                      <Badge
                        variant={isToday ? "secondary" : "outline"}
                        className="mt-1 text-xs px-1.5 py-0"
                      >
                        {totalEvents}
                      </Badge>
                    )}
                  </div>

                  {/* Events */}
                  <div className="space-y-1.5">
                    {isLoading ? (
                      <EventSkeleton />
                    ) : (
                      <>
                        {/* All-day events first */}
                        {dayEvents.allDay.map(ev => (
                          <EventCard
                            key={ev.id}
                            event={ev}
                            onEdit={setEditingEvent}
                            onDelete={handleDelete}
                            onRemoveFromMine={handleRemoveFromMine}
                            isOwnCalendar={selectedEmail === "self"}
                          />
                        ))}
                        {/* Timed events */}
                        {dayEvents.timed.map(ev => (
                          <EventCard
                            key={ev.id}
                            event={ev}
                            onEdit={setEditingEvent}
                            onDelete={handleDelete}
                            onRemoveFromMine={handleRemoveFromMine}
                            isOwnCalendar={selectedEmail === "self"}
                          />
                        ))}
                        {/* Empty state */}
                        {totalEvents === 0 && (
                          <div className="rounded-lg border border-dashed border-border/50 px-3 py-4 text-center">
                            <p className="text-xs text-muted-foreground">No events</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
